import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit'
import z from 'zod'
import { TeacherType } from '../firebase/types-teacher'
import { TeacherEmploymentSettings } from './types'
import { RootState } from './store'
import { SchoolBookingTypes } from '../firebase/types-bookings'

export const SchoolModalKeys = {
    allBookableTeachers: 'allBookableTeachers',
    bookableTeachers: 'bookableTeachers',
    sendRequests: 'sendRequests',
    bookingDetails: 'bookingDetails',
    createBooking: 'createBooking',
    cancelBooking: 'cancelBooking',
    replacementOpportunity: 'replacementOpportunity',
    reportIncident: 'reportIncident',
    reportIssue: 'reportIssue',
    teacherProfile: 'teacherProfile',

    cancelPendingReplacement: 'cancelPendingReplacement',
} as const
// export type SchoolModalKey =
//     (typeof SchoolModalKeys)[keyof typeof SchoolModalKeys]

const bookingDetailPayloadSchema = z.object({
    date: z.string(),
    placementUid: z.string(),
    teacherUid: z.string(),
    interest: z.enum(TeacherEmploymentSettings),
})
const datePayloadSchema = z.object({
    date: z.number(),
})
const modalSchema = z.object({
    open: z.boolean(),
})

const createBookingSchema = z.object({
    teacherUid: z.string(),
    dates: z.array(z.number()).optional(),
    interest: z.enum(TeacherEmploymentSettings),
    type: z.enum(SchoolBookingTypes).optional(),
})
export type CreateBookingProps = z.infer<typeof createBookingSchema>

const modalEntrySchema = z.object({
    cancelBooking: modalSchema.extend({
        payload: bookingDetailPayloadSchema,
    }),
    bookingDetails: modalSchema.extend({
        payload: bookingDetailPayloadSchema,
    }),
    reportIncident: modalSchema.extend({
        payload: bookingDetailPayloadSchema,
    }),
    reportIssue: modalSchema.extend({
        payload: bookingDetailPayloadSchema,
    }),
    allBookableTeachers: modalSchema.extend({
        payload: z.undefined(),
    }),
    bookableTeachers: modalSchema.extend({
        payload: datePayloadSchema,
    }),

    sendRequests: modalSchema.extend({
        payload: z.object({
            date: z.number().optional(),
        }),
    }),
    createBooking: modalSchema.extend({
        payload: createBookingSchema,
    }),
    // teacherProfile: modalSchema.extend({
    //     payload: z.object({
    //         uid: z.string(),
    //     }),
    // }),
    cancelPendingReplacement: modalSchema.extend({
        payload: z.object({
            requestId: z.string(),
        }),
    }),
})
export type ModalEntry = z.infer<typeof modalEntrySchema>
export type SchoolModalKey = keyof ModalEntry

const modalArgsSchema = z.discriminatedUnion('key', [
    z.object({
        key: z.enum([
            'cancelBooking',
            'bookingDetails',
            'reportIncident',
            'reportIssue',
        ]),
        payload: bookingDetailPayloadSchema,
    }),
    z.object({
        key: z.literal('allBookableTeachers'),
        payload: z.undefined(),
    }),
    z.object({
        key: z.literal('bookableTeachers'),
        payload: datePayloadSchema,
    }),
    z.object({
        key: z.literal('sendRequests'),
        payload: z.object({
            date: z.number().optional(),
        }),
    }),
    z.object({
        key: z.literal('createBooking'),
        payload: z.object({
            teacherUid: z.string(),
            dates: z.array(z.number()).optional(),
            interest: z.enum(TeacherEmploymentSettings),
            type: z.enum(SchoolBookingTypes).optional(),
        }),
    }),
    // z.object({
    //     key: z.literal('teacherProfile'),
    //     payload: z.object({
    //         uid: z.string(),
    //     }),
    // }),
    z.object({
        key: z.literal('cancelPendingReplacement'),
        payload: z.object({
            requestId: z.string(),
        }),
    }),
])
type ModalArgs = z.infer<typeof modalArgsSchema>

type Params = {
    type: TeacherType | null | undefined
    q: string
}
export interface ISchoolModalSlice {
    modalStack: Partial<ModalEntry>
    params: {
        bookableTeachers: Params
        allBookables: Params
    }
    disableBtns: boolean
}

export const initialState: ISchoolModalSlice = {
    modalStack: {},
    params: {
        bookableTeachers: {
            type: undefined,
            q: '',
        },
        allBookables: {
            type: undefined,
            q: '',
        },
    },
    disableBtns: false,
}

export const schoolModalSlice = createSlice({
    name: 'schoolModal',
    initialState,
    reducers: {
        pushModal: (state, action: PayloadAction<ModalArgs>) => {
            Object.assign(state.modalStack, {
                [action.payload.key]: {
                    open: true,
                    payload: action.payload.payload,
                },
            })
        },
        closeModal: (state, action: PayloadAction<SchoolModalKey>) => {
            const entry = state.modalStack[action.payload]
            if (entry) {
                entry.open = false
            }
        },
        removeModalPayload: (state, action: PayloadAction<SchoolModalKey>) => {
            const entry = state.modalStack[action.payload]
            if (entry) {
                entry.payload = undefined
            }
        },
        closeAllModals: (state) => {
            state.modalStack = {}
            return state
            // return {
            //     modalStack: {},
            //     bookableTeachers: state.bookableTeachers,
            //     disableBtns: state.disableBtns,
            // }
        },

        setModalDisableBtns: (state, action: PayloadAction<boolean>) => {
            state.disableBtns = action.payload
        },

        updateParams: (
            state,
            action: PayloadAction<{
                key: keyof ISchoolModalSlice['params']
                data: Partial<{
                    type: TeacherType | null
                    q: string
                }>
            }>
        ) => {
            Object.assign(state.params[action.payload.key], action.payload.data)
        },
    },
})

export const closeModalWithTimeout =
    (key: SchoolModalKey, duration: number = 500) =>
    (dispatch: Dispatch) => {
        dispatch(closeModal(key))

        // Delay payload delete so that modal can close with data
        setTimeout(() => {
            dispatch(removeModalPayload(key))
        }, duration)
    }

export const selectSchoolModalStack = (state: RootState) =>
    state.schoolModal.modalStack
export const selectModalEntry =
    (modalKey: SchoolModalKey) => (state: RootState) =>
        state.schoolModal.modalStack[modalKey]

export const selectBookingDetails = (state: RootState) =>
    state.schoolModal.modalStack.bookingDetails?.payload
export const selectBookingInterest = (state: RootState) =>
    state.schoolModal.modalStack.bookingDetails?.payload.interest
export const selectBookableTeachersPayload = (state: RootState) =>
    state.schoolModal.modalStack.bookableTeachers?.payload

export const selectCreateBookingDetails = (state: RootState) =>
    state.schoolModal.modalStack.createBooking?.payload

export const {
    pushModal,
    closeModal,
    removeModalPayload,
    updateParams,
    closeAllModals,
    setModalDisableBtns,
} = schoolModalSlice.actions
export default schoolModalSlice.reducer
